import React, { Suspense, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./App.scss";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import "./sass/bootstrap.scss";
import "@staff1.sippi/ui-components/dist/index.css";
import { Helmet } from "react-helmet";
import { useAppSelector } from "./store";
import { ProtectedRoute } from "./components/Common/ProtectedRoute";
import { MainLoader } from "./components/Common/Ui/MainLoader";
import { NoModuleFound } from "./components/Common/NoModuleFound";
import ViewEnquiryDetails from "./components/queryList/ViewEnquiryDetails";
import My404Route from "./components/Common/My404Route";
import { NotFoundPage } from "./components/Common/NotFoundPage";
import { AppConstants } from "./config/appConstants";
import { Deskphone } from "./components/Dashboard/Deskphone/Deskphone";
import { DepartmentConfig } from "./components/Dashboard/Department/DepartmentConfig";
import { Footer, Header } from "@staff1.sippi/ui-components";

const Dashboard = React.lazy(() => import("./components/Dashboard/Dashboard"));
const Department = React.lazy(
  () => import("./components/Dashboard/Department/Department")
);

const FlightsFormPromise = import("./components/Tabs/FlightsForm");
const FlightsForm = React.lazy(() => FlightsFormPromise);

const HotelsQueryFormPromise = import("./components/Tabs/HotelsQueryForm");
const HotelsQueryForm = React.lazy(() => HotelsQueryFormPromise);

const TrainQueryFormPromise = import("./components/Tabs/TrainQueryForm");
const TrainQueryForm = React.lazy(() => TrainQueryFormPromise);

const BusQueryFormPromise = import("./components/Tabs/BusQueryForm");
const BusQueryForm = React.lazy(() => BusQueryFormPromise);

const CruiseQueryFormPromise = import("./components/Tabs/CruiseQueryForm");
const CruiseQueryForm = React.lazy(() => CruiseQueryFormPromise);

const InsuranceQueryFormPromise = import(
  "./components/Tabs/InsuranceQueryForm"
);
const InsuranceQueryForm = React.lazy(() => InsuranceQueryFormPromise);

const OtherEnquiryPromise = import("./components/Tabs/OtherEnquiry");
const OtherEnquiry = React.lazy(() => OtherEnquiryPromise);

const VisaEnquiryTabPromise = import("./components/Tabs/VisaEnquiryTab");
const VisaEnquiryTab = React.lazy(() => VisaEnquiryTabPromise);

const CrmWrapper = React.lazy(() => import("./CrmWrapper"));

const EnquiryList = React.lazy(
  () => import("./components/queryList/QueryList")
);

const ViewChangeRequestDetails = React.lazy(
  () => import("./components/queryList/ChangeRequest/ViewChangeRequestDetails")
);

function CrmRouter() {
  const { data: siteData } = useAppSelector((store) => store.siteData);
  const [activeMenus, setActiveMenus] = useState<any>([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);

  useEffect(() => {
    if (siteData) {
      const activeMenu = siteData?.activeModules?.map((menu, index) => {
        let element;
        if (menu.siteModule.module_name === "FlightsForm") {
          element = <FlightsForm />;
        }
        if (menu.siteModule.module_name === "Hotels") {
          element = <HotelsQueryForm />;
        }
        if (menu.siteModule.module_name === "Bus") {
          element = <BusQueryForm />;
        }
        if (menu.siteModule.module_name === "Train") {
          element = <TrainQueryForm />;
        }
        if (menu.siteModule.module_name === "Cruise") {
          element = <CruiseQueryForm />;
        }
        if (menu.siteModule.module_name === "Insurance") {
          element = <InsuranceQueryForm />;
        }
        if (menu.siteModule.module_name === "OtherEnquiry") {
          element = <OtherEnquiry />;
        }
        if (menu.siteModule.module_name === "VisaEnquiry") {
          element = <VisaEnquiryTab />;
        }

        if (!element) {
          return null;
        } else {
          return {
            path: menu.siteModule.module_name,
            element,
          };
        }
      });
      setActiveMenus(activeMenu.filter((menu) => menu));
      setLoadingRoutes(false);
    }
  }, [siteData]);

  const router = createBrowserRouter(
    [
      {
        path: "/",
        element: <CrmWrapper className="menu.siteModule-tab" />,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <EnquiryList />
              </ProtectedRoute>
            ),
          },
          {
            path: "/enquiry/:id",
            element: (
              <ProtectedRoute>
                <ViewEnquiryDetails />
              </ProtectedRoute>
            ),
          },
          {
            path: "/crm-change-request/:changeRequestUUID",
            element: (
              <ProtectedRoute>
                <ViewChangeRequestDetails />
              </ProtectedRoute>
            ),
          },
          {
            path: "/department",
            element: <Department />,
          },
          {
            path: "/dashboard",
            element: <Dashboard />,
          },
          {
            path: "/deskphone",
            element: <Deskphone />,
          },
          {
            path: "/department-config",
            element: <DepartmentConfig />,
          },

          {
            path: "*",
            element: <My404Route />,
          },
          ...activeMenus,
          {
            path: "/notFound",
            element: <NotFoundPage />,
          },
        ],
      },
      {
        path: "*",
        element: <Navigate to="/notFound" replace />,
      },
    ],
    {
      basename: AppConstants.appBaseUrl,
    }
  );

  const headerProps = siteData?.reactHeaderFooterProps?.headerProps;
  const footerProps = siteData?.reactHeaderFooterProps?.footerProps;

  return (
    <React.Fragment>
      {headerProps && (
        <Header
          version={headerProps.version || "header_V1"}
          // version={"header_V2"}
          siteData={headerProps.siteData}
          moduleData={headerProps.moduleData}
          userData={headerProps.userData}
          moduleUrls={headerProps.moduleUrls}
          dashboardUrls={headerProps.dashboardUrls}
          moduleMenus={headerProps.moduleMenus}
        />
      )}

      <ToastContainer />
      <Suspense fallback={<MainLoader loading={true} />}>
        <Helmet>
          <title>{siteData?.siteName || ""} | CRM</title>
        </Helmet>
        {activeMenus.length > 0 && !loadingRoutes ? (
          <RouterProvider router={router} />
        ) : loadingRoutes ? (
          <MainLoader loading={loadingRoutes} />
        ) : (
          <NoModuleFound />
        )}
      </Suspense>

      {footerProps && (
        <Footer
          version={footerProps.version || "footer_V1"}
          // version={"footer_V3"}
          siteData={footerProps.siteData}
          moduleUrls={footerProps.moduleUrls}
        />
      )}
    </React.Fragment>
  );
}

export default CrmRouter;
